import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import { startQboOAuth } from '@/features/integrations/qboOAuth';
import { QuickbooksPageShell } from './QuickbooksPageShell';

/**
 * Public landing page Intuit sends users to from the Atlas marketplace
 * listing on apps.com when the user clicks "Get app now" / "Connect"
 * (or "Reconnect" after a refresh-token expiry).
 *
 * Registered with Intuit as the **Connect/Reconnect URL** (App Settings →
 * Production / Development). Must be reachable without Atlas
 * authentication so the Intuit reviewer can fetch it.
 *
 * Flow:
 *   1. If the user is signed in to Atlas and can manage Integrations,
 *      they can click "Continue to QuickBooks" to immediately start the
 *      OAuth authorize flow. We bounce them through Intuit and back into
 *      `/integrations/qbo/callback` for the token exchange.
 *   2. If they're signed in but don't have Integrations access, we
 *      explain that only people granted that page can connect QuickBooks
 *      at the organization level.
 *   3. If they're not signed in, we send them to /auth/sign-in with a
 *      redirect back here so the OAuth flow resumes after sign-in.
 */
export function QuickbooksConnectPage() {
  const { session, profile, loading } = useAuth();
  const navigate = useNavigate();

  const handleConnect = () => {
    const err = startQboOAuth();
    if (err) toast.error(err);
  };

  const handleSignIn = () => {
    navigate('/auth/sign-in', {
      state: { from: { pathname: '/quickbooks/connect' } },
    });
  };

  const isSignedIn = !!session && !loading;
  const canConnect =
    !!profile?.is_active && canRead(profile.role, 'integrations', profile.section_access);

  return (
    <QuickbooksPageShell
      eyebrow="QuickBooks Online · Connect"
      title="Connect QuickBooks Online to Atlas"
      description={
        <p>
          Atlas syncs your QuickBooks Online chart of accounts and journal
          entries every 15 minutes using OAuth 2.0. We never store your QuickBooks
          password — we only hold a refresh token in encrypted storage,
          and you can revoke access at any time from QuickBooks Online or
          from Atlas.
        </p>
      }
    >
      <ol className="space-y-3 rounded-lg border border-border bg-card p-5 text-sm text-foreground">
        <li className="flex gap-3">
          <span className="text-muted-foreground">1.</span>
          <span>Sign in to Atlas with your Virro Google Workspace account.</span>
        </li>
        <li className="flex gap-3">
          <span className="text-muted-foreground">2.</span>
          <span>
            Authorize Atlas in QuickBooks. You&rsquo;ll be sent to Intuit
            to grant read access, then redirected back here.
          </span>
        </li>
        <li className="flex gap-3">
          <span className="text-muted-foreground">3.</span>
          <span>
            Atlas pulls your chart of accounts and the last 36 months of
            your Profit &amp; Loss report automatically.
          </span>
        </li>
      </ol>

      {!isSignedIn && (
        <div className="space-y-3">
          <Button onClick={handleSignIn}>Sign in to Atlas</Button>
          <p className="text-xs text-muted-foreground">
            After you sign in we&rsquo;ll bring you back here to finish
            connecting QuickBooks.
          </p>
        </div>
      )}

      {isSignedIn && canConnect && (
        <div className="space-y-3">
          <Button onClick={handleConnect}>Continue to QuickBooks</Button>
          <p className="text-xs text-muted-foreground">
            We&rsquo;ll redirect you to <code>appcenter.intuit.com</code> to
            authorize Atlas. After you approve, you&rsquo;ll come back to
            the Atlas integrations page.
          </p>
        </div>
      )}

      {isSignedIn && !canConnect && (
        <div className="rounded-lg border border-fresh-salmon/40 bg-fresh-salmon/10 px-4 py-3 text-sm text-fresh-salmon-dark">
          Only people with <strong>Integrations</strong> access can connect
          QuickBooks for the organization. Ask a super admin to grant that
          page on Roles &amp; users.
        </div>
      )}
    </QuickbooksPageShell>
  );
}
